import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Users,
  ChartBar,
  ShieldCheck,
  ArrowLeft,
  Crown,
  UserCircle,
  SignIn,
} from "@phosphor-icons/react";
import api from "@/lib/api";

function MetricCard({ label, value, sub }) {
  return (
    <div className="border-2 border-black p-4">
      <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500">
        {label}
      </div>
      <div className="font-heading text-3xl font-bold tracking-tight mt-1">{value}</div>
      {sub && <div className="font-body text-xs text-neutral-400 mt-1">{sub}</div>}
    </div>
  );
}

function UsersTab({ users, total, currentUserRole, onSetRole, onLoginAs }) {
  return (
    <div>
      <div className="p-4 border-b-2 border-black bg-neutral-50">
        <span className="font-heading text-lg font-bold">{total}</span>
        <span className="font-body text-sm text-neutral-500 ml-2">total users</span>
      </div>
      <div className="overflow-x-auto">
        {users.map((u) => (
          <div
            key={u._id}
            className="border-b border-black py-3 px-4 flex items-center gap-3"
            data-testid={`admin-user-${u._id}`}
          >
            <div className="shrink-0">
              {u.role === "super_admin" ? (
                <Crown size={20} weight="fill" className="text-yellow-500" />
              ) : u.role === "admin" ? (
                <ShieldCheck size={20} weight="fill" className="text-blue-600" />
              ) : (
                <UserCircle size={20} weight="bold" className="text-neutral-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-sm font-bold truncate">{u.name || "—"}</div>
              <div className="font-body text-xs text-neutral-500 truncate">{u.email}</div>
              <div className="font-body text-xs text-neutral-400">{u.phone || "No phone"}</div>
            </div>
            <div className="shrink-0 text-right">
              <div className={`inline-block px-2 py-0.5 border border-black font-body text-xs font-semibold uppercase ${
                u.role === "super_admin" ? "bg-yellow-100" : u.role === "admin" ? "bg-blue-100" : "bg-white"
              }`}>
                {u.role}
              </div>
              <div className="font-body text-xs text-neutral-400 mt-1">
                {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
              </div>
            </div>
            {/* Role toggle - only super_admin can change roles, can't change super_admin */}
            {currentUserRole === "super_admin" && u.role !== "super_admin" && (
              <button
                onClick={() => onSetRole(u._id, u.role === "admin" ? "user" : "admin")}
                className={`shrink-0 px-2 py-1 border-2 border-black font-body text-xs font-bold uppercase ${
                  u.role === "admin"
                    ? "bg-white hover:bg-red-50"
                    : "bg-black text-white hover:bg-neutral-800"
                }`}
                data-testid={`toggle-role-${u._id}`}
              >
                {u.role === "admin" ? "Demote" : "Make Admin"}
              </button>
            )}
            {u.role !== "super_admin" && (
              <button
                onClick={() => onLoginAs(u._id)}
                className="shrink-0 px-2 py-1 border-2 border-black bg-[#002FA7] text-white font-body text-xs font-bold uppercase hover:bg-blue-800"
                data-testid={`login-as-${u._id}`}
                title={`Login as ${u.name}`}
              >
                <SignIn size={14} weight="bold" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MetricsTab({ metrics }) {
  if (!metrics) return <div className="p-8 text-center font-body text-neutral-500">Loading...</div>;

  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <MetricCard label="Total Users" value={metrics.total_users} sub={`+${metrics.new_users_week} this week`} />
        <MetricCard label="Total Scans" value={metrics.total_verdicts} sub={`+${metrics.verdicts_week} this week`} />
        <MetricCard label="Total Hauls" value={metrics.total_hauls} />
        <MetricCard label="Items Tracked" value={metrics.total_haul_items} sub={`${metrics.items_sold} sold`} />
      </div>

      {/* Verdict Distribution */}
      {metrics.verdict_distribution && Object.keys(metrics.verdict_distribution).length > 0 && (
        <div className="border-4 border-black p-4">
          <div className="font-heading text-lg font-bold mb-3">Verdict Distribution</div>
          <div className="space-y-2">
            {Object.entries(metrics.verdict_distribution).map(([verdict, count]) => {
              const total = Object.values(metrics.verdict_distribution).reduce((a, b) => a + b, 0);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const color = verdict === "GRAB" ? "#00E676" : verdict === "SKIP" ? "#FF1744" : "#FFEA00";
              return (
                <div key={verdict} className="flex items-center gap-3">
                  <div className="font-heading text-sm font-bold w-20 uppercase">{verdict}</div>
                  <div className="flex-1 h-6 border-2 border-black bg-white overflow-hidden">
                    <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                  <div className="font-body text-sm font-semibold w-16 text-right">{count} ({pct}%)</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top Brands */}
      {metrics.top_brands && metrics.top_brands.length > 0 && (
        <div className="border-4 border-black p-4">
          <div className="font-heading text-lg font-bold mb-3">Top Scanned Brands</div>
          <div className="space-y-1">
            {metrics.top_brands.map((b, i) => (
              <div key={i} className="flex justify-between py-1 border-b border-neutral-200 last:border-0">
                <span className="font-body text-sm">{b.brand || "Unknown"}</span>
                <span className="font-heading text-sm font-bold">{b.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState("metrics");
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [usersRes, metricsRes] = await Promise.all([
        api.get("/admin/users").catch(() => ({ data: { users: [], total: 0 } })),
        api.get("/admin/metrics").catch(() => ({ data: null })),
      ]);
      setUsers(usersRes.data.users || []);
      setTotalUsers(usersRes.data.total || 0);
      setMetrics(metricsRes.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "super_admin")) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user, loadData]);

  const handleSetRole = async (userId, newRole) => {
    try {
      await api.post("/admin/set-role", { user_id: userId, role: newRole });
      loadData();
    } catch {
      // ignore
    }
  };

  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const handleLoginAs = async (userId) => {
    try {
      await api.post("/admin/login-as", { user_id: userId });
      await checkAuth();
      navigate("/scan");
    } catch {
      // ignore
    }
  };

  if (authLoading) return null;
  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white" data-testid="admin-page">
      <div className="sticky top-0 z-50 bg-white border-b-4 border-black py-4 px-6 flex items-center gap-3">
        <a href="/scan" className="border-2 border-black p-2" data-testid="admin-back">
          <ArrowLeft size={18} weight="bold" />
        </a>
        <h1 className="font-heading text-2xl font-black tracking-tighter uppercase">
          Admin
        </h1>
        <div className={`ml-auto px-2 py-1 border-2 border-black font-body text-xs font-bold uppercase ${
          user.role === "super_admin" ? "bg-yellow-100" : "bg-blue-100"
        }`}>
          {user.role}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b-4 border-black">
        <button
          onClick={() => setTab("metrics")}
          className={`flex-1 p-4 flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm tracking-wide border-r-2 border-black ${
            tab === "metrics" ? "bg-black text-white" : "hover:bg-neutral-50"
          }`}
          data-testid="admin-tab-metrics"
        >
          <ChartBar size={18} weight="bold" />
          Metrics
        </button>
        <button
          onClick={() => setTab("users")}
          className={`flex-1 p-4 flex items-center justify-center gap-2 font-heading font-bold uppercase text-sm tracking-wide ${
            tab === "users" ? "bg-black text-white" : "hover:bg-neutral-50"
          }`}
          data-testid="admin-tab-users"
        >
          <Users size={18} weight="bold" />
          Users ({totalUsers})
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center font-body text-neutral-500">Loading...</div>
      ) : tab === "metrics" ? (
        <MetricsTab metrics={metrics} />
      ) : (
        <UsersTab
          users={users}
          total={totalUsers}
          currentUserRole={user.role}
          onSetRole={handleSetRole}
          onLoginAs={handleLoginAs}
        />
      )}
    </div>
  );
}
